import { ReactNode } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { OrderCellStatus } from 'components/Orders/Orders/Components/OrderCellStatus';

import { DisplayPrices, Order } from '../types';
import { CreditCardPaymentMethod } from './CreditCardPaymentMethod';

const Title = ({ text }: { text: string }) => {
  return (
    <div className="text-sm font-medium text-base-foreground w-44 shrink-0">
      {text}
    </div>
  );
};

export const Value = ({
  value,
  dataTestId,
  enableCopy = false
}: {
  value: string;
  dataTestId?: string;
  enableCopy?: boolean;
}) => {
  return (
    <div className="flex items-center gap-1 min-w-0" data-testid={dataTestId}>
      <div
        className="text-sm text-base-mutedForeground font-light truncate min-w-0 flex-1"
        title={value}
      >
        {value}
      </div>
      {enableCopy && value !== 'N/A' && value !== '' && (
        <button
          onClick={() => {
            navigator.clipboard.writeText(value);
          }}
          title="Copy value"
          aria-label="Copy value"
          className="text-base-mutedForeground hover:text-base-foreground transition-colors shrink-0"
        >
          <FontAwesomeIcon icon="fa-regular fa-copy" />
        </button>
      )}
    </div>
  );
};

const DetailRow = ({
  title,
  value,
  dataTestId,
  enableCopy = false,
  children
}: {
  title: string;
  value: string;
  dataTestId?: string;
  enableCopy?: boolean;
  children?: ReactNode;
}) => {
  return (
    <div className="flex items-center gap-x-8 min-w-0">
      <Title text={title} />
      {children ?? (
        <Value value={value} dataTestId={dataTestId} enableCopy={enableCopy} />
      )}
    </div>
  );
};

export const OrderDetails = ({
  order,
  publisherId,
  displayPrices
}: {
  order?: Order;
  publisherId: string | undefined;
  displayPrices: DisplayPrices | null;
}) => {
  if (!order || !displayPrices) return null;

  return (
    <div className="flex flex-col gap-8 p-6 pb-8 border border-base-sidebarBorder rounded-lg">
      <div className="flex items-center gap-4">
        <h2
          className="font-medium text-xl text-base-primary"
          data-testid="orderDetailsTotalPayment"
        >
          Total Payment: {order.currencySymbol}
          {displayPrices.totalPayment}
        </h2>
        <OrderCellStatus
          status={order.status}
          dataTestId="orderDetailsStatus"
        />
      </div>

      <div className="grid grid-cols-2 gap-y-5 w-[80%] pl-4 gap-x-8">
        <DetailRow
          title="Date"
          value={new Date(order.createdAt).toLocaleString()}
          dataTestId="orderDetailsDate"
        />
        {order.paymentMethod?.primaryMethod && (
          <DetailRow
            title="Payment Method"
            value={order.paymentMethod?.primaryMethod || ''}
            dataTestId="orderDetailsPaymentMethod"
          >
            {order.paymentMethod && (
              <CreditCardPaymentMethod paymentMethod={order.paymentMethod} />
            )}
          </DetailRow>
        )}

        <DetailRow
          title="Country"
          value={order.playerGeo.countryCode2}
          dataTestId="orderDetailsCountry"
        />
        <DetailRow
          title="Tax Rate"
          value={`${order.taxRatePoints / 100}%`}
          dataTestId="orderDetailsTaxRate"
        />

        <DetailRow
          title="State"
          value={order.playerGeo.state}
          dataTestId="orderDetailsState"
        />
        <DetailRow
          title="Player ID"
          value={order.playerId}
          dataTestId="orderDetailsPlayerId"
        />

        {order.zipCode && (
          <DetailRow
            title="Postal Code"
            value={order.zipCode || ''}
            dataTestId="orderDetailsPostalCode"
          />
        )}
        <DetailRow
          title="Player Email"
          value={order.playerEmail || 'N/A'}
          dataTestId="orderDetailsPlayerEmail"
        />
        <DetailRow
          title="Order ID"
          value={order._id}
          dataTestId="orderDetailsOrderId"
        />

        <DetailRow
          title="Offer Name"
          value={order.bundleName}
          dataTestId="orderDetailsOfferName"
        />

        <DetailRow
          title="Offer External ID"
          value={order.bundleSku}
          dataTestId="orderDetailsOfferExternalId"
          enableCopy={true}
        />
        <DetailRow
          title="Project"
          value={publisherId ?? ''}
          dataTestId="orderDetailsProject"
        />

        <DetailRow
          title="Subtotal"
          value={`${order.currencySymbol}${displayPrices.amountNetTotal}`}
          dataTestId="orderDetailsSubtotal"
        />
        <DetailRow
          title="Receipt Number"
          value={order.receiptId || 'N/A'}
          dataTestId="orderDetailsReceiptNumber"
        />
        <DetailRow
          enableCopy={true}
          title="Publisher Transaction ID"
          value={order.publisherPurchaseId || 'N/A'}
          dataTestId="orderDetailsTransactionId"
        />
        <DetailRow
          title="Device OS"
          value={order.userAgent?.OS || 'N/A'}
          dataTestId="orderDetailsDeviceOs"
        />
      </div>
    </div>
  );
};
